/**
 * Bottom sheet / modal to list and add comments on a Studio clip.
 */
import {useState} from 'react'
import {ActivityIndicator, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import * as Dialog from '#/components/Dialog'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import {atoms as a, useTheme} from '#/alf'
import {useSession} from '#/state/session'
import {
  useStudioVideoCommentsQuery,
  useStudioCreateVideoComment,
} from '#/state/queries/studio'
import * as TextField from '#/components/forms/TextField'

export function ClipsCommentsDialog({
  control,
  videoId,
  videoTitle,
}: {
  control: ReturnType<typeof Dialog.useDialogControl>
  videoId: string | null
  videoTitle: string
}) {
  const {_} = useLingui()
  const t = useTheme()
  const {currentAccount} = useSession()
  const {data: comments = [], isLoading} = useStudioVideoCommentsQuery(videoId)
  const createComment = useStudioCreateVideoComment(videoId ?? '')
  const [text, setText] = useState('')
  const canSubmit = text.trim().length > 0 && !createComment.isPending

  const onSubmit = () => {
    if (!videoId || !canSubmit) return
    const trimmed = text.trim()
    createComment.mutate(
      {
        text: trimmed,
        author_id: currentAccount?.did,
        author_handle: currentAccount?.handle ?? undefined,
      },
      {
        onSuccess: () => {
          setText('')
        },
      },
    )
  }

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Comments`)}>
        <Dialog.Header>
          <Dialog.HeaderText>
            <Trans>Comments</Trans>
            {videoTitle ? ` · ${videoTitle}` : ''}
          </Dialog.HeaderText>
        </Dialog.Header>
        {!videoId ? (
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            <Trans>Select a clip to view comments.</Trans>
          </Text>
        ) : (
          <>
            {isLoading ? (
              <View style={[a.py_xl, a.align_center]}>
                <ActivityIndicator />
              </View>
            ) : comments.length === 0 && !createComment.isSuccess ? (
              <Text style={[a.text_md, t.atoms.text_contrast_medium, a.pb_md]}>
                <Trans>No comments yet. Be the first!</Trans>
              </Text>
            ) : (
              <View style={[a.gap_md, a.pb_md]}>
                {comments.map(c => (
                  <View
                    key={c.id}
                    style={[a.p_sm, a.rounded_md, t.atoms.bg_contrast_25]}>
                    <Text style={[a.text_sm, a.font_semi_bold, t.atoms.text]}>
                      {c.author_handle ?? c.author_id}
                    </Text>
                    <Text style={[a.text_sm, t.atoms.text_contrast_high]}>
                      {c.text}
                    </Text>
                  </View>
                ))}
              </View>
            )}
            {currentAccount && (
              <View style={[a.gap_sm]}>
                <TextField.LabelText>
                  <Trans>Add a comment</Trans>
                </TextField.LabelText>
                <TextField.Root>
                  <TextField.Input
                    label={_(msg`Comment`)}
                    placeholder={_(msg`Write a comment...`)}
                    value={text}
                    onChangeText={setText}
                    multiline
                    numberOfLines={2}
                  />
                </TextField.Root>
                <Button
                  label={_(msg`Post comment`)}
                  onPress={onSubmit}
                  color="primary"
                  size="small"
                  disabled={!canSubmit}>
                  <ButtonText>
                    <Trans>Post comment</Trans>
                  </ButtonText>
                </Button>
              </View>
            )}
            {!currentAccount && (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                <Trans>Sign in to comment.</Trans>
              </Text>
            )}
          </>
        )}
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}
